/**
 * RewardConfigDisplay Component
 *
 * Read-only display for RewardConfig on the RewardDetail page.
 * Uses the same info-grid/info-row pattern as other detail sections.
 *
 * Annual limits are shown from the annual_pool ("Yearly Budget") section.
 * Monthly pool limits are shown from the monthly_pool ("Monthly Budget") section.
 * The word "pool" does not appear in the admin-facing UI.
 */

import React from 'react';
import type { RewardConfig, LimitPoolConfig } from '@/app/types/rewards';
import { getLimitTypeLabel } from '@/app/types/rewards';

import './RewardConfigDisplay.scss';

interface RewardConfigDisplayProps {
  config: RewardConfig | null;
}

function formatPoolLimit(pool: LimitPoolConfig, limitType: string): string {
  if (limitType === 'count' && pool.limit_count != null) {
    return `${pool.limit_count.toLocaleString()} redemptions`;
  }
  if (pool.limit_usd != null) {
    return `$${pool.limit_usd.toLocaleString()}`;
  }
  return '—';
}

export const RewardConfigDisplay: React.FC<RewardConfigDisplayProps> = ({ config }) => {
  if (!config) {
    return (
      <div className="empty-config">
        <span className="muted">No reward configuration set</span>
      </div>
    );
  }

  return (
    <div className="reward-config-display">
      {/* Limits (monthly only) */}
      <div className="config-section">
        <h5 className="config-section-title">Limits</h5>
        <div className="info-grid">
          <div className="info-row">
            <span className="info-label">Limit Type</span>
            <span className="info-value">
              <span className={`limit-type-badge ${config.limit_type}`}>
                {getLimitTypeLabel(config.limit_type)}
              </span>
            </span>
          </div>
          {config.limit_type === 'dollar' && (
            <div className="info-row">
              <span className="info-label">Monthly Limit</span>
              <span className="info-value">
                {config.monthly_limit_usd != null
                  ? `$${config.monthly_limit_usd.toFixed(2)}`
                  : '—'}
              </span>
            </div>
          )}
          {config.limit_type === 'count' && (
            <div className="info-row">
              <span className="info-label">Monthly Limit</span>
              <span className="info-value">
                {config.monthly_limit_count != null
                  ? `${config.monthly_limit_count} redemptions`
                  : '—'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Yearly Budget */}
      <div className="config-section">
        <h5 className="config-section-title">Yearly Budget</h5>
        <div className="info-grid">
          {config.annual_pool ? (
            <>
              <div className="info-row">
                <span className="info-label">Yearly Limit</span>
                <span className="info-value">
                  {formatPoolLimit(config.annual_pool, config.limit_type)}/year
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Budget ID</span>
                <span className="info-value mono">{config.annual_pool.pool_id}</span>
              </div>
            </>
          ) : (
            <div className="info-row">
              <span className="info-label">Yearly Limit</span>
              <span className="info-value">No yearly limit</span>
            </div>
          )}
        </div>
      </div>

      {/* Monthly Budget */}
      <div className="config-section">
        <h5 className="config-section-title">Monthly Budget</h5>
        <div className="info-grid">
          {config.monthly_pool ? (
            <>
              <div className="info-row">
                <span className="info-label">Monthly Limit</span>
                <span className="info-value">
                  {formatPoolLimit(config.monthly_pool, config.limit_type)}/month
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Budget ID</span>
                <span className="info-value mono">{config.monthly_pool.pool_id}</span>
              </div>
            </>
          ) : (
            <div className="info-row">
              <span className="info-label">Monthly Limit</span>
              <span className="info-value">No monthly shared limit</span>
            </div>
          )}
        </div>
      </div>

      {/* Availability */}
      <div className="config-section">
        <h5 className="config-section-title">Availability</h5>
        <div className="info-grid">
          <div className="info-row">
            <span className="info-label">Available</span>
            <span className="info-value">
              <span className={`availability-badge ${config.available !== false ? 'yes' : 'no'}`}>
                {config.available !== false ? 'Yes' : 'No'}
              </span>
            </span>
          </div>
          {config.availability_message && (
            <div className="info-row">
              <span className="info-label">Message</span>
              <span className="info-value">{config.availability_message}</span>
            </div>
          )}
          <div className="info-row">
            <span className="info-label">Show Message on Card</span>
            <span className="info-value">{config.show_message_on_card ? 'Yes' : 'No'}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Collect Pre-Redemption Data</span>
            <span className="info-value">{config.collect_pre_redemption_data ? 'Yes' : 'No'}</span>
          </div>
        </div>
      </div>

      {/* Completion Points */}
      {config.completion_points != null && config.completion_points > 0 && (
        <div className="config-section">
          <h5 className="config-section-title">Completion Points</h5>
          <div className="info-grid">
            <div className="info-row">
              <span className="info-label">Points Awarded</span>
              <span className="info-value">{config.completion_points.toLocaleString()} pts</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardConfigDisplay;
